import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getLocale } from "~/paraglide/runtime";
import * as m from "~/paraglide/messages";
import { useSession } from "~/lib/auth-client";
import { orpc } from "~/lib/orpc-client";

type DeclarationMode = "withdrawal" | "termination";
type TerminationType = "ordinary_termination" | "extraordinary_termination";
type FormStage = "details" | "review" | "success";

type FieldName =
  | "consumerName"
  | "accountEmail"
  | "confirmationEmail"
  | "contractReference"
  | "requestedEndDate"
  | "extraordinaryReason";

interface ConsumerContractDeclarationFormProps {
  mode: DeclarationMode;
}

interface FieldValues {
  consumerName: string;
  accountEmail: string;
  confirmationEmail: string;
  contractReference: string;
  requestedEndDate: string;
  extraordinaryReason: string;
}

interface Receipt {
  receiptId: string;
  receivedAt: string;
  receiptDeliveryState: "pending" | "sent" | "failed";
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FIELD_ELEMENT_IDS: Record<FieldName, string> = {
  consumerName: "consumer-name",
  accountEmail: "account-email",
  confirmationEmail: "confirmation-email",
  contractReference: "contract-reference",
  requestedEndDate: "requested-end-date",
  extraordinaryReason: "extraordinary-reason",
};

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function requestedDateToInstant(value: string): string | undefined {
  if (!value) return undefined;
  return `${value}T23:59:59.000Z`;
}

export function ConsumerContractDeclarationForm({ mode }: ConsumerContractDeclarationFormProps) {
  const { data: session } = useSession();
  const tierQuery = useQuery({
    ...orpc.billing.currentTier.queryOptions(),
    enabled: Boolean(session?.user),
    retry: 0,
  });
  const mutation = useMutation(orpc.billing.submitConsumerContractDeclaration.mutationOptions());

  const [stage, setStage] = React.useState<FormStage>("details");
  const [terminationType, setTerminationType] = React.useState<TerminationType>("ordinary_termination");
  const [values, setValues] = React.useState<FieldValues>({
    consumerName: "",
    accountEmail: "",
    confirmationEmail: "",
    contractReference: "",
    requestedEndDate: "",
    extraordinaryReason: "",
  });
  const [errors, setErrors] = React.useState<Partial<Record<FieldName, string>>>({});
  const [receipt, setReceipt] = React.useState<Receipt | null>(null);
  const [statusMessage, setStatusMessage] = React.useState("");
  const requestIdRef = React.useRef<string | null>(null);
  // React Query updates `isPending` on the next render. Keep a synchronous
  // per-review latch so repeated activation in the same event turn cannot
  // submit the same declaration twice before that render occurs.
  const finalSubmitLatchRef = React.useRef(false);
  const reviewHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const successHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const fieldRefs = React.useRef<Partial<Record<FieldName, HTMLInputElement | HTMLTextAreaElement | null>>>({});
  const pendingErrorFocusRef = React.useRef<FieldName | null>(null);
  const prefilledRef = React.useRef(false);

  React.useEffect(() => {
    const prefill = tierQuery.data;
    if (!prefill || prefilledRef.current) return;
    prefilledRef.current = true;
    setValues((current) => ({
      ...current,
      consumerName: current.consumerName || prefill.accountName,
      accountEmail: current.accountEmail || prefill.accountEmail,
      confirmationEmail: current.confirmationEmail || prefill.accountEmail,
      contractReference: current.contractReference || prefill.contractReference || "",
    }));
  }, [tierQuery.data]);

  React.useEffect(() => {
    if (stage === "review") reviewHeadingRef.current?.focus();
    if (stage === "success") successHeadingRef.current?.focus();
  }, [stage]);

  React.useEffect(() => {
    const field = pendingErrorFocusRef.current;
    if (!field) return;
    pendingErrorFocusRef.current = null;
    const element = fieldRefs.current[field] ?? document.getElementById(FIELD_ELEMENT_IDS[field]);
    element?.focus();
  }, [errors]);

  function updateField(field: FieldName, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  }

  function validate(): boolean {
    const nextErrors: Partial<Record<FieldName, string>> = {};
    if (!values.consumerName.trim()) nextErrors.consumerName = m.consumer_declaration_error_required();
    if (!EMAIL_PATTERN.test(values.accountEmail.trim())) nextErrors.accountEmail = m.consumer_declaration_error_email();
    if (!EMAIL_PATTERN.test(values.confirmationEmail.trim())) nextErrors.confirmationEmail = m.consumer_declaration_error_email();
    if (!values.contractReference.trim()) nextErrors.contractReference = m.consumer_declaration_error_required();
    if (
      mode === "termination" &&
      values.requestedEndDate &&
      values.requestedEndDate < todayIsoDate()
    ) {
      nextErrors.requestedEndDate = m.consumer_declaration_error_future_date();
    }
    if (
      mode === "termination" &&
      terminationType === "extraordinary_termination" &&
      !values.extraordinaryReason.trim()
    ) {
      nextErrors.extraordinaryReason = m.consumer_declaration_error_required();
    }

    setErrors(nextErrors);
    const firstError = Object.keys(nextErrors)[0] as FieldName | undefined;
    if (firstError) {
      pendingErrorFocusRef.current = firstError;
      setStatusMessage(m.consumer_declaration_fix_errors());
      return false;
    }
    setStatusMessage("");
    return true;
  }

  function handleDetailsSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!validate()) return;
    requestIdRef.current ??= crypto.randomUUID();
    setStage("review");
  }

  function handleFinalSubmit() {
    if (finalSubmitLatchRef.current || mutation.isPending || !requestIdRef.current) return;
    finalSubmitLatchRef.current = true;
    const declarationType = mode === "withdrawal" ? "withdrawal" : terminationType;
    setStatusMessage(m.consumer_declaration_submitting());
    mutation.mutate({
      id: requestIdRef.current,
      declarationType,
      consumerName: values.consumerName.trim(),
      accountEmail: values.accountEmail.trim().toLowerCase(),
      confirmationEmail: values.confirmationEmail.trim().toLowerCase(),
      contractReference: values.contractReference.trim(),
      ...(mode === "termination" && values.requestedEndDate
        ? { requestedEndDate: requestedDateToInstant(values.requestedEndDate) }
        : {}),
      ...(declarationType === "extraordinary_termination"
        ? { extraordinaryReason: values.extraordinaryReason.trim() }
        : {}),
      locale: getLocale() as "en" | "de" | "fr" | "es" | "it" | "nl" | "pl" | "fi" | "da" | "sv",
    }, {
      onSuccess: (result) => {
        setReceipt(result);
        setStatusMessage(m.consumer_declaration_received_status());
        setStage("success");
      },
      onError: () => {
        // A failed request remains reviewable and uses the same idempotency
        // key, so deliberately release the latch only for this retry path.
        finalSubmitLatchRef.current = false;
        setStatusMessage(m.consumer_declaration_submit_error());
      },
    });
  }

  function declarationLabel(): string {
    if (mode === "withdrawal") return m.consumer_declaration_type_withdrawal();
    return terminationType === "ordinary_termination"
      ? m.consumer_declaration_type_ordinary()
      : m.consumer_declaration_type_extraordinary();
  }

  function intendedEndLabel(): string {
    if (mode === "withdrawal") return "";
    return values.requestedEndDate || m.consumer_declaration_earliest_date();
  }

  function receiptText(): string {
    if (!receipt) return "";
    const lines = [
      m.consumer_declaration_receipt_title(),
      `${m.consumer_declaration_receipt_id()}: ${receipt.receiptId}`,
      `${m.consumer_declaration_type_label()}: ${declarationLabel()}`,
      `${m.consumer_declaration_consumer_name()}: ${values.consumerName.trim()}`,
      `${m.consumer_declaration_account_email()}: ${values.accountEmail.trim().toLowerCase()}`,
      `${m.consumer_declaration_contract_reference()}: ${values.contractReference.trim()}`,
      `${m.consumer_declaration_confirmation_email()}: ${values.confirmationEmail.trim().toLowerCase()}`,
      `${m.consumer_declaration_received_at()}: ${new Date(receipt.receivedAt).toISOString()}`,
      `${m.consumer_declaration_final_action_activated()}: ${finalButtonLabel}`,
    ];
    if (mode === "termination") {
      lines.push(`${m.consumer_declaration_intended_end()}: ${intendedEndLabel()}`);
    }
    if (terminationType === "extraordinary_termination" && values.extraordinaryReason.trim()) {
      lines.push(`${m.consumer_declaration_extraordinary_reason()}: ${values.extraordinaryReason.trim()}`);
    }
    return lines.join("\n");
  }

  function downloadReceipt() {
    if (!receipt) return;
    const blob = new Blob([receiptText()], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `alchema-declaration-${receipt.receiptId}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const firstButtonLabel = mode === "withdrawal"
    ? m.consumer_declaration_withdraw_first()
    : m.consumer_declaration_termination_first();
  const finalButtonLabel = mode === "withdrawal"
    ? m.consumer_declaration_withdraw_final()
    : m.consumer_declaration_termination_final();

  return (
    <section className="mx-auto max-w-2xl px-6 py-12" data-declaration-mode={mode}>
      <div className="card p-6 sm:p-8">
        {stage === "details" && (
          <form onSubmit={handleDetailsSubmit} noValidate aria-describedby="consumer-declaration-intro">
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
              {mode === "withdrawal" ? m.consumer_declaration_withdrawal_title() : m.consumer_declaration_termination_title()}
            </h1>
            <p id="consumer-declaration-intro" className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {mode === "withdrawal" ? m.consumer_declaration_withdrawal_intro() : m.consumer_declaration_termination_intro()}
            </p>

            {mode === "termination" && (
              <fieldset className="mt-6">
                <legend className="text-sm font-medium" style={{ color: "var(--text)" }}>{m.consumer_declaration_type_label()}</legend>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  {(["ordinary_termination", "extraordinary_termination"] as const).map((type) => (
                    <label key={type} className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
                      <input
                        type="radio"
                        name="termination-type"
                        value={type}
                        checked={terminationType === type}
                        onChange={() => {
                          setTerminationType(type);
                          if (type === "ordinary_termination") updateField("extraordinaryReason", "");
                        }}
                      />
                      <span className="text-sm" style={{ color: "var(--text)" }}>
                        {type === "ordinary_termination" ? m.consumer_declaration_type_ordinary() : m.consumer_declaration_type_extraordinary()}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            <div className="mt-6 grid gap-4">
              <Field
                id="consumer-name"
                label={m.consumer_declaration_consumer_name()}
                value={values.consumerName}
                error={errors.consumerName}
                inputRef={(node) => { fieldRefs.current.consumerName = node; }}
                onChange={(value) => updateField("consumerName", value)}
                autoComplete="name"
              />
              <Field
                id="account-email"
                label={m.consumer_declaration_account_email()}
                value={values.accountEmail}
                error={errors.accountEmail}
                inputRef={(node) => { fieldRefs.current.accountEmail = node; }}
                onChange={(value) => updateField("accountEmail", value)}
                type="email"
                autoComplete="email"
              />
              <Field
                id="confirmation-email"
                label={m.consumer_declaration_confirmation_email()}
                value={values.confirmationEmail}
                error={errors.confirmationEmail}
                inputRef={(node) => { fieldRefs.current.confirmationEmail = node; }}
                onChange={(value) => updateField("confirmationEmail", value)}
                type="email"
                autoComplete="email"
              />
              <Field
                id="contract-reference"
                label={m.consumer_declaration_contract_reference()}
                hint={m.consumer_declaration_contract_reference_hint()}
                value={values.contractReference}
                error={errors.contractReference}
                inputRef={(node) => { fieldRefs.current.contractReference = node; }}
                onChange={(value) => updateField("contractReference", value)}
                autoComplete="off"
              />

              {mode === "termination" && (
                <Field
                  id="requested-end-date"
                  label={m.consumer_declaration_requested_end_date()}
                  hint={m.consumer_declaration_optional_earliest()}
                  value={values.requestedEndDate}
                  error={errors.requestedEndDate}
                  inputRef={(node) => { fieldRefs.current.requestedEndDate = node; }}
                  onChange={(value) => updateField("requestedEndDate", value)}
                  type="date"
                  min={todayIsoDate()}
                />
              )}

              {mode === "termination" && terminationType === "extraordinary_termination" && (
                <label htmlFor="extraordinary-reason" className="block text-sm font-medium" style={{ color: "var(--text)" }}>
                  {m.consumer_declaration_extraordinary_reason()}
                  <textarea
                    ref={(node) => { fieldRefs.current.extraordinaryReason = node; }}
                    id="extraordinary-reason"
                    value={values.extraordinaryReason}
                    onChange={(event) => updateField("extraordinaryReason", event.target.value)}
                    maxLength={1000}
                    rows={4}
                    required
                    aria-invalid={Boolean(errors.extraordinaryReason)}
                    aria-describedby={errors.extraordinaryReason ? "extraordinary-reason-error" : undefined}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                    style={{ borderColor: errors.extraordinaryReason ? "var(--error)" : "var(--border-subtle)", background: "var(--surface-secondary)", color: "var(--text)" }}
                  />
                  {errors.extraordinaryReason && <span id="extraordinary-reason-error" role="alert" className="mt-1 block text-xs" style={{ color: "var(--error)" }}>{errors.extraordinaryReason}</span>}
                </label>
              )}
            </div>

            <button type="submit" className="btn btn-accent mt-6 w-full" disabled={mutation.isPending} aria-disabled={mutation.isPending}>
              {firstButtonLabel}
            </button>
          </form>
        )}

        {stage === "review" && (
          <div>
            <h1 ref={reviewHeadingRef} tabIndex={-1} className="text-2xl font-bold tracking-tight outline-none" style={{ color: "var(--text)" }}>
              {m.consumer_declaration_review_title()}
            </h1>
            <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>{m.consumer_declaration_review_intro()}</p>
            <dl className="mt-6 grid gap-3 rounded-lg border p-4 text-sm" style={{ borderColor: "var(--border-subtle)" }}>
              <ReceiptRow label={m.consumer_declaration_type_label()} value={declarationLabel()} />
              <ReceiptRow label={m.consumer_declaration_consumer_name()} value={values.consumerName.trim()} />
              <ReceiptRow label={m.consumer_declaration_account_email()} value={values.accountEmail.trim().toLowerCase()} />
              <ReceiptRow label={m.consumer_declaration_confirmation_email()} value={values.confirmationEmail.trim().toLowerCase()} />
              <ReceiptRow label={m.consumer_declaration_contract_reference()} value={values.contractReference.trim()} />
              {mode === "termination" && <ReceiptRow label={m.consumer_declaration_intended_end()} value={intendedEndLabel()} />}
              {terminationType === "extraordinary_termination" && <ReceiptRow label={m.consumer_declaration_extraordinary_reason()} value={values.extraordinaryReason.trim()} />}
            </dl>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
              <button type="button" className="btn btn-secondary sm:flex-1" onClick={() => setStage("details")} disabled={mutation.isPending} aria-disabled={mutation.isPending}>
                {m.consumer_declaration_edit()}
              </button>
              <button type="button" className="btn btn-accent sm:flex-1" onClick={handleFinalSubmit} disabled={mutation.isPending} aria-disabled={mutation.isPending}>
                {mutation.isPending ? m.consumer_declaration_submitting() : finalButtonLabel}
              </button>
            </div>
          </div>
        )}

        {stage === "success" && receipt && (
          <div>
            <h1 ref={successHeadingRef} tabIndex={-1} className="text-2xl font-bold tracking-tight outline-none" style={{ color: "var(--text)" }}>
              {m.consumer_declaration_success_title()}
            </h1>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {m.consumer_declaration_success_body()}
            </p>
            {receipt.receiptDeliveryState === "failed" && (
              <p role="status" className="mt-3 text-sm" style={{ color: "var(--warning)" }}>
                {m.consumer_declaration_email_failed()}
              </p>
            )}
            <pre data-testid="consumer-declaration-receipt" className="mt-6 whitespace-pre-wrap break-words rounded-lg border p-4 text-xs" style={{ borderColor: "var(--border-subtle)", color: "var(--text)", background: "var(--surface-secondary)" }}>
              {receiptText()}
            </pre>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button type="button" className="btn btn-secondary sm:flex-1" onClick={() => window.print()}>{m.consumer_declaration_print()}</button>
              <button type="button" className="btn btn-accent sm:flex-1" onClick={downloadReceipt}>{m.consumer_declaration_download()}</button>
            </div>
          </div>
        )}

        <p aria-live="polite" role="status" className="mt-4 min-h-5 text-sm" style={{ color: mutation.isError ? "var(--error)" : "var(--text-muted)" }}>
          {statusMessage}
        </p>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  hint,
  value,
  error,
  inputRef,
  onChange,
  type = "text",
  ...props
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  error?: string;
  inputRef: (node: HTMLInputElement | null) => void;
  onChange: (value: string) => void;
  type?: React.HTMLInputTypeAttribute;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "id" | "value" | "onChange" | "type">) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <label htmlFor={id} className="block text-sm font-medium" style={{ color: "var(--text)" }}>
      {label}
      {hint && <span id={hintId} className="ml-1 text-xs font-normal" style={{ color: "var(--text-muted)" }}>{hint}</span>}
      <input
        {...props}
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={type === "email" ? 254 : id === "consumer-name" ? 120 : 128}
        aria-invalid={Boolean(error)}
        aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
        className="mt-1 min-h-11 w-full rounded-lg border px-3 py-2 text-sm"
        style={{ borderColor: error ? "var(--error)" : "var(--border-subtle)", background: "var(--surface-secondary)", color: "var(--text)" }}
      />
      {error && <span id={errorId} role="alert" className="mt-1 block text-xs" style={{ color: "var(--error)" }}>{error}</span>}
    </label>
  );
}

function ReceiptRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[minmax(9rem,1fr)_2fr]">
      <dt className="font-medium" style={{ color: "var(--text-muted)" }}>{label}</dt>
      <dd className="break-words" style={{ color: "var(--text)" }}>{value}</dd>
    </div>
  );
}
